import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import SocialLinks from "@/components/common/SocialLinks";
import { stagger, fadeUp } from "@/lib/motion";

const INFO = [
  {
    icon: Phone,
    title: "Phone",
    lines: [
      { text: "+91-87960 27362", href: "tel:+918796027362" },
      { text: "+91-120-4373786", href: "tel:+911204373786" },
    ],
    note: "Mon–Sat · business hours below",
  },
  { icon: Mail, title: "Email", lines: [{ text: "help@autocarex.in", href: "mailto:help@autocarex.in" }] },
  { icon: MapPin, title: "Office", lines: [{ text: "440, F Block, Sector 63" }, { text: "Noida, Uttar Pradesh - 201301" }, { text: "India" }] },
];
const HOURS = [
  { day: "Monday – Friday", time: "10:00 AM – 6:00 PM" },
  { day: "Saturday", time: "11:00 AM – 4:00 PM" },
  { day: "Sunday", time: "Closed" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createContact(form);
      toast.success(res.message || "Message sent successfully");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="Contact" description="Get in touch with AutoCareX — phone, email, office address and business hours." />

      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_50%_0%,rgba(156,163,175,0.10),transparent_60%)]" />
        <div className="acx-container relative px-4 py-16 text-center sm:px-6 lg:px-10 lg:py-24">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">Get in touch</span>
            <h1 className="mt-3 font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-6xl">Contact Us</h1>
            <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-white/65">
              Questions, feedback or partnership ideas? We'd love to hear from you.
            </p>
            <SocialLinks className="mt-7 justify-center" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[var(--acx-bg)]">
        <div className="acx-container grid grid-cols-1 gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-10">
          {/* Info + hours */}
          <div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {INFO.map((c) => (
                <motion.div key={c.title} variants={fadeUp} className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E11B22]/15 text-[#E11B22]"><c.icon size={19} /></span>
                  <p className="mt-3 text-[13px] uppercase tracking-[0.12em] text-white/45">{c.title}</p>
                  {c.lines.map((l) => (
                    l.href ? (
                      <a key={l.text} href={l.href} className="mt-1 block break-words text-[14px] text-white/80 hover:text-white">{l.text}</a>
                    ) : (
                      <p key={l.text} className="mt-1 break-words text-[14px] text-white/80">{l.text}</p>
                    )
                  ))}
                  {c.note && <p className="mt-2 text-[12px] leading-relaxed text-white/40">{c.note}</p>}
                </motion.div>
              ))}
            </motion.div>

            <div className="mt-5 rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6">
              <div className="flex items-center gap-2"><Clock size={18} className="text-white/70" /><h3 className="text-[15px] font-semibold">Business Hours</h3></div>
              <div className="mt-4 space-y-2.5">
                {HOURS.map((h) => (
                  <div key={h.day} className="flex items-center justify-between border-b border-[color:var(--acx-border)] pb-2.5 text-[14px] last:border-0">
                    <span className="text-white/65">{h.day}</span>
                    <span className="font-medium text-white">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6" data-testid="contact-follow-us">
              <h3 className="text-[15px] font-semibold">Follow us</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-white/50">Stay updated with the latest from AutoCareX.</p>
              <SocialLinks className="mt-4" />
            </div>

            <div className="mt-5 overflow-hidden rounded-[16px] border border-[color:var(--acx-border)]" data-testid="contact-map-embed">
              <iframe
                title="AutoCareX location"
                src="https://www.google.com/maps?q=440%2C%20F%20Block%2C%20Sector%2063%2C%20Noida%2C%20Uttar%20Pradesh%20201301&output=embed"
                className="h-[260px] w-full grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div>
            <div className="rounded-[20px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6 sm:p-8">
              <h2 className="font-display text-2xl font-bold">Send us a message</h2>
              <p className="mt-2 text-[14px] text-white/55">We typically respond within one business day.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[13px] text-white/70">Name *</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] text-white/70">Phone</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="+91" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] text-white/70">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="you@email.com" />
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] text-white/70">Message *</label>
                  <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="How can we help?" />
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-white px-6 py-3.5 text-[15px] font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60">
                  {submitting ? "Sending..." : (<>Send Message <Send size={16} /></>)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
